import { type FC, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios, { type AxiosError } from "axios";
import { axiosPostCamelToSnake } from "@root/shared/utils/axiosPostCamelToSnake";
import { showAlert } from "@root/shared/utils";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { Presentation } from "./Presentation";
import { buildSchema, type HoldEnqueteFormValues } from "./schema";
import { answersToFormValues, formValuesToAnswers, postToConfirm } from "./utils";
import { type Props } from "./types";

// 1. defaultValues.answers（前回入力）を react-hook-form で使用できる値に変換
// 2. submit 時に一時保存 API へ POST、成功したら確認画面へブラウザ遷移
export const HoldEnquete: FC<Props> = ({
  questions,
  defaultValues,
  cacheUrl,
  confirmUrl,
  backHref,
  csrfToken,
  holdPeriodText,
  baseYear,
  baseMonth,
}) => {
  const schema = useMemo(() => buildSchema(questions), [questions]);
  const initialValues = useMemo(
    () => answersToFormValues(defaultValues, questions),
    [defaultValues, questions],
  );

  const methods = useForm<HoldEnqueteFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const handleOnSubmit = (values: HoldEnqueteFormValues): Promise<void> => {
    const answers = formValuesToAnswers(values, questions);

    return axiosPostCamelToSnake
      .post(cacheUrl, {
        formClientFrontendClientHoldEnquete: {
          reopenDate: defaultValues.reopenDate,
          answers,
        },
      })
      .then(() => {
        postToConfirm(confirmUrl, defaultValues.reopenDate, csrfToken);
      })
      .catch((error: AxiosError<{ errors?: string[] }>) => {
        // 422 はサーバー側検証エラー: メッセージをまとめて表示する
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          const messages = error.response.data?.errors ?? [];
          showAlert(messages.length > 0 ? messages.join("\n") : "保存に失敗しました");
          return;
        }
        // それ以外（ネットワーク・5xx 等）は共通のエラー通知
        sendErrorLog(new Error(error.message || "休止アンケートの一時保存に失敗しました。"));
        showAlert("送信に失敗しました。時間をおいて再度お試しください。");
      });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleOnSubmit)}>
        <Presentation
          questions={questions}
          backHref={backHref}
          holdPeriodText={holdPeriodText}
          baseYear={baseYear}
          baseMonth={baseMonth}
        />
      </form>
    </FormProvider>
  );
};
